import { ArrowLeft, PaintBucket, SaveIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Design_, useDesignsApi } from 'src/api/db/apiDesigns'
import { useQuoteItemApi } from 'src/api/db/apiQuoteItem'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button, Card } from 'src/shared/components/ui'
import { Badge } from 'src/shared/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { useLocalization } from 'src/shared/hooks/useLocalization'

import { DesignArea } from './components/DesignArea'
import { ArtworkPicker } from './components/ArtworkPicker'
import { ColorPicker } from './components/ColorPicker'
import { DecorationPicker } from './components/DecorationPicker'
import { FilterPicker } from './components/FilterPicker'
import { IllustrationPicker } from './components/IllustrationPicker'
import { useParams, unstable_useBlocker as useBlocker } from 'react-router-dom'
import { PageContainer } from 'src/shared/components/PageContainer'
import { QuoteSubItem_, useQuoteSubItemApi } from 'src/api/db/apiQuoteSubItem'
import { DecorationOnboarding } from './DecorationOnboarding'
import { FullScreenLoader } from 'src/shared/components/FullScreenLoader'
import { useGetDecorationInfo } from 'src/api/server/useGetDecorationInfo'
import { useOrgsApi } from 'src/api/db/apiOrgs'

const width = 600
const height = 600

export const EditDecorationPage = () => {
  const { t } = useLocalization()
  const { order_item_id, order_sub_item_id } = useParams<{
    order_item_id: string
    order_sub_item_id?: string
  }>()

  const { updateQuoteSubItem, deleteQuoteSubItem, createQuoteSubItem } =
    useQuoteSubItemApi()
  const { updateDesign, createDesign } = useDesignsApi()
  const { quoteItem, updateQuoteItem } = useQuoteItemApi({
    quote_item_id: Number(order_item_id),
  })
  const { organizationById } = useOrgsApi({
    org_id: quoteItem?.data?.quote?.org_id!,
  })
  const companyUrl = organizationById?.data?.company_url

  const quote_item = quoteItem?.data
  const quote_sub_item = quote_item?.quote_sub_item?.find(
    (quote_sub_item) => quote_sub_item.id === Number(order_sub_item_id)
  )

  const initialValuesDesign = useRef<any>(null)
  const initialValuesQuoteSubItem = useRef<any>(null)
  const initialValuesProductVariantColorId = useRef<number | null>(null)
  const isCreatingDesign = useRef(false)

  const [hasChanges, setHasChanges] = useState(false)
  const [selectedProductVariantColorId, setSelectedProductVariantColorId] =
    useState<number | null>(null)
  const [design, setDesign] = useState<Partial<Design_>>({})
  const [quoteSubItem, setQuoteSubItem] = useState<Partial<QuoteSubItem_>>({})

  const { decorations } = useGetDecorationInfo({
    product_id: quote_item?.product?.id!,
    item_quantity: quoteSubItem.quantity ?? 1,
    item_colors: quoteSubItem.d_colors ?? 1,
  })

  // Create design if it doesn't exist (backward compatibility fix)
  useEffect(() => {
    if (!quote_sub_item || !quote_item) return

    const designData = quote_sub_item.design?.[0]

    const colors =
      quote_item.product?.product_variant?.[0]?.product_variant_color
    const selectedColor =
      colors?.find((c) => c.id === quote_item.product_variant_color_id) ||
      colors?.[0]
    const sortedImages = [...(selectedColor?.product_image ?? [])].sort(
      (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
    )
    const defaultImageUrl = sortedImages[0]?.image_url ?? null
    const defaultDesignUrl = companyUrl
      ? `https://cdn.brandfetch.io/${companyUrl}/fallback/404/w/400?c=1idPWY0AiNqr7y_koY9`
      : null

    if (!designData && !isCreatingDesign.current) {
      isCreatingDesign.current = true

      createDesign.mutate(
        {
          quote_item_id: quote_item.id,
          quote_sub_item_id: quote_sub_item.id,
          image_url: defaultImageUrl,
          design_url: defaultDesignUrl,
          w: 100,
          h: 100,
          x: 600 / 2 - 50,
          y: 600 / 2 - 50,
        },
        {
          onSettled: () => {
            isCreatingDesign.current = false
          },
        }
      )
      return
    }

    if (!designData) return

    // Default illustration (and artwork when already available) if nothing selected
    const resolvedDesign = {
      ...designData,
      image_url: designData.image_url || defaultImageUrl,
      design_url: designData.design_url || defaultDesignUrl,
    }

    setDesign(resolvedDesign)
    setQuoteSubItem(quote_sub_item)
    setSelectedProductVariantColorId(
      quote_item.product_variant_color_id ?? null
    )

    initialValuesDesign.current = resolvedDesign
    initialValuesQuoteSubItem.current = quote_sub_item
    initialValuesProductVariantColorId.current =
      quote_item.product_variant_color_id ?? null
  }, [quote_sub_item, quote_item])

  // Fill default artwork once company URL loads (without resetting other design edits)
  useEffect(() => {
    if (!companyUrl) return
    const defaultDesignUrl = `https://cdn.brandfetch.io/${companyUrl}/fallback/404/w/400?c=1idPWY0AiNqr7y_koY9`
    setDesign((prev) => {
      if (!prev?.id || prev.design_url) return prev
      if (
        initialValuesDesign.current &&
        !initialValuesDesign.current.design_url
      ) {
        initialValuesDesign.current = {
          ...initialValuesDesign.current,
          design_url: defaultDesignUrl,
        }
      }
      return { ...prev, design_url: defaultDesignUrl }
    })
  }, [companyUrl])

  // If artwork is still empty after logos load, treat the first auto-pick as initial
  // so we don't mark the page dirty for a default selection.
  const handleSelectDesignUrl = (url: string | null) => {
    setDesign((prev) => {
      if (!prev.design_url && url && !initialValuesDesign.current?.design_url) {
        initialValuesDesign.current = {
          ...initialValuesDesign.current,
          design_url: url,
        }
      }
      return { ...prev, design_url: url }
    })
  }
  // Watch for changes
  useEffect(() => {
    const timer = setTimeout(() => {
      const designChanged =
        JSON.stringify(design) !== JSON.stringify(initialValuesDesign.current)

      const quoteSubItemChanged =
        JSON.stringify(quoteSubItem) !==
        JSON.stringify(initialValuesQuoteSubItem.current)

      const colorChanged =
        selectedProductVariantColorId !==
        initialValuesProductVariantColorId.current

      setHasChanges(designChanged || quoteSubItemChanged || colorChanged)
    }, 300)

    return () => clearTimeout(timer)
  }, [design, quoteSubItem, selectedProductVariantColorId])

  const shouldBlock = useCallback(() => hasChanges, [hasChanges])
  const blocker = useBlocker(shouldBlock)

  const product_images = useMemo(() => {
    const colors = quote_item?.product?.product_variant[0].product_variant_color

    return (
      colors?.find((c) => c.id === selectedProductVariantColorId) || colors?.[0]
    )
  }, [quote_item, selectedProductVariantColorId])

  // Handle browser refresh/close with beforeunload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasChanges])

  const handleSave = async () => {
    if (!hasChanges) return
    if (quote_sub_item?.id == null || quote_item?.id == null) return

    const chosenDecoration = decorations?.find(
      (d) => d.print_code === quoteSubItem.print_code
    )

    const colorChanged =
      selectedProductVariantColorId !==
      initialValuesProductVariantColorId.current
    if (
      colorChanged &&
      selectedProductVariantColorId != null &&
      quote_item?.quote_id
    ) {
      await updateQuoteItem.mutateAsync({
        id: quote_item.id,
        quote_id: quote_item.quote_id,
        product_variant_color_id: selectedProductVariantColorId,
      })
    }

    await updateDesign?.mutateAsync(
      {
        id: design?.id,
        quote_item_id: quote_item?.id!,
        x: design?.x,
        y: design?.y,
        w: design?.w,
        h: design?.h,
        rotation_x: design?.rotation_x,
        rotation_y: design?.rotation_y,
        rotation_z: design?.rotation_z,
        opacity: design?.opacity,
        brightness: design?.brightness,
        saturation: design?.saturation,
        hue: design?.hue,
        colorFilter: design?.colorFilter,
        image_url: design?.image_url,
        design_url: design?.design_url,
        warp_transform: design?.warp_transform,
      },
      { onSuccess: () => {} }
    )

    const existingSetupCost = quote_item?.quote_sub_item?.find((qsi) => {
      return qsi.type === 'setup_cost' && qsi.quote_item_id === quote_item.id
    })

    if (existingSetupCost) {
      await deleteQuoteSubItem.mutateAsync(existingSetupCost.id)
    }

    await updateQuoteSubItem.mutateAsync({
      id: quote_sub_item.id,
      d_colors: quoteSubItem.d_colors ?? undefined,
      print_code: quoteSubItem.print_code ?? undefined,
      description: chosenDecoration?.info?.name || '-',
      unit_price: chosenDecoration
        ? Math.round(chosenDecoration.unit_cost ?? 0)
        : undefined,
    })

    if (chosenDecoration?.setup_cost && chosenDecoration.setup_cost > 0) {
      await createQuoteSubItem.mutateAsync({
        quote_item_id: quote_item.id,
        type: 'setup_cost',
        unit_price: chosenDecoration.setup_cost,
        quantity: 1,
        description: `Setup cost`,
      })
    }

    setHasChanges(false)
    initialValuesDesign.current = JSON.parse(JSON.stringify(design))
    initialValuesQuoteSubItem.current = JSON.parse(JSON.stringify(quoteSubItem))
    initialValuesProductVariantColorId.current = selectedProductVariantColorId
  }

  const isLoading =
    quoteItem?.isPending ||
    !quote_item ||
    !quote_sub_item ||
    !product_images ||
    createDesign.isPending

  if (isLoading) {
    return <FullScreenLoader />
  }

  if (
    quote_item?.description !== 'Obestämt tryck || Unspecified print' &&
    !quoteSubItem.print_code
  ) {
    return (
      <DecorationOnboarding
        quoteItem={quote_item}
        quoteSubItem={quote_sub_item}
        productImages={product_images}
      />
    )
  }

  return (
    <>
      <PageContainer width="xl" className="h-dvh">
        <Row className="my-auto gap-4 w-full justify-center">
          <Column className="gap-4 w-1/3">
            <Row className="gap-2">
              <Button
                variant="secondary"
                onClick={() => window.history.back()}
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go back
              </Button>

              <Button
                size="sm"
                variant="primary"
                onClick={handleSave}
                disabled={
                  !hasChanges ||
                  updateDesign?.isPending ||
                  updateQuoteItem?.isPending
                }
                pulsatingBorder={hasChanges}
              >
                <SaveIcon className="w-4 h-4 mr-2" />
                {updateDesign?.isPending
                  ? t('Saving...', 'Sparar...')
                  : t('Save changes', 'Spara ändringar')}
              </Button>

              {hasChanges && (
                <Badge variant="orange">
                  {t('Unsaved changes', 'Osparade ändringar')}
                </Badge>
              )}
            </Row>

            <Card>
              <Column className="gap-2 mt-2">
                <p className="text-md font-normal">
                  {quote_item?.product?.item_name}
                </p>
                {/* <p
                  className={`text-sm text-gray-500 ${
                    !showFullDescription ? 'line-clamp-2' : ''
                  }`}
                  dangerouslySetInnerHTML={{
                    __html: quote_item?.product?.item_description || '',
                  }}
                />

                {quote_item?.product?.item_description &&
                  quote_item.product.item_description.length > 100 && (
                    <button
                      className="text-xs text-blue-500 hover:underline self-start"
                      onClick={() =>
                        setShowFullDescription(!showFullDescription)
                      }
                    >
                      {showFullDescription
                        ? t('Show less', 'Visa mindre')
                        : t('Show more', 'Visa mer')}
                    </button>
                  )} */}
                <p className="text-sm text-gray-500">
                  {quote_item?.product?.price}{' '}
                  {organizationById?.data?.currency ?? 'SEK'}
                </p>
              </Column>
            </Card>

            <Card>
              <Column className="gap-2">
                <Row className="items-center gap-2">
                  <PaintBucket className="w-4 h-4" />
                  <p className="text-sm">{t('Color', 'Färg')}</p>

                  <Badge>
                    {selectedProductVariantColorId
                      ? quote_item?.product?.product_variant[0]?.product_variant_color.find(
                          (c) => c.id === selectedProductVariantColorId
                        )?.color
                      : ''}
                  </Badge>
                </Row>

                <ColorPicker
                  selectedColorId={selectedProductVariantColorId}
                  productVariantColors={
                    quote_item?.product?.product_variant[0]
                      ?.product_variant_color ?? []
                  }
                  onColorChange={setSelectedProductVariantColorId}
                />
              </Column>
            </Card>

            <DecorationPicker
              quoteSubItem={quoteSubItem}
              selectedPrintCode={quoteSubItem?.print_code}
              decorations={decorations}
              setPrintCode={(val) =>
                setQuoteSubItem((prev) => ({ ...prev, ...val }))
              }
            />
          </Column>

          <DesignArea
            width={width}
            height={height}
            design_={design}
            setDesign_={setDesign}
            quote_sub_item_id={quote_sub_item?.id}
            quote_item_id={quote_item?.id}
            product_name={
              t(quote_item?.product?.item_name_translations) ||
              quote_item?.product?.item_name ||
              t('Product', 'Produkt')
            }
            mockup_urls={quote_sub_item?.mockup_urls}
            decoration_name={
              decorations?.find(
                (d) => d?.print_code === quoteSubItem?.print_code
              )?.info?.name ?? null
            }
            onDeleteMockup={(url) => {
              if (!quote_sub_item?.mockup_urls?.length || !quote_sub_item?.id)
                return
              const newUrls = quote_sub_item.mockup_urls.filter(
                (u) => u !== url
              )
              updateQuoteSubItem.mutate({
                id: quote_sub_item.id,
                mockup_urls: newUrls,
              })
            }}
          />

          <Column className="gap-4 w-1/3 mt-12">
            <ArtworkPicker
              org_id={quoteItem?.data?.quote?.org_id!}
              companyUrl={companyUrl}
              allowSkip
              selectedDesignUrl={design?.design_url}
              onSelectDesignUrl={handleSelectDesignUrl}
              onColorCountChange={(colorCount) =>
                setQuoteSubItem((prev) => ({
                  ...prev,
                  d_colors: colorCount,
                }))
              }
            />

            <IllustrationPicker
              productImages={product_images}
              selectedProductImage={design?.image_url}
              onSelectProductImage={(image_url) =>
                setDesign((prev) => ({ ...prev, image_url }))
              }
            />
            <FilterPicker design={design} setDesign={setDesign} />
          </Column>
        </Row>
      </PageContainer>

      <Dialog
        open={blocker.state === 'blocked'}
        onOpenChange={(open) => !open && blocker.reset?.()}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('Unsaved changes', 'Osparade ändringar')}
            </DialogTitle>

            <DialogDescription>
              {t(
                'You have unsaved changes. Are you sure you want to leave without saving?',
                'Du har osparade ändringar. Är du säker på att du vill lämna utan att spara?'
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2">
            <Button variant="secondary" onClick={() => blocker.reset?.()}>
              {t('Cancel', 'Avbryt')}
            </Button>

            <Button variant="destructive" onClick={() => blocker.proceed?.()}>
              {t('Leave without saving', 'Lämna utan att spara')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
